import { FC, useState, useEffect } from 'react';
import theme from 'config/theme';
import { ENV_CONFIG } from 'config/environment';
import Spacing from 'components/Spacing';
import CheckBox from 'components/CheckBox';
import Icon, { IconNames } from 'components/Icon';
import { Body, UtilityText } from 'components/Typography';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { noop, getUrlParams } from 'helpers/utils';
import { Details } from './Details';
import { CompetitivePrimaryBanner } from './CompetitivePrimaryCandidate/CompetitivePrimaryBanner';
import {
  Container,
  Subtitle,
  StyledImage,
  CardContent,
  ShowMoreLessRow,
  CandidateGrid,
  GridCheckbox,
  CandidatePhoto,
  PlusContainer,
  Content,
  ShowMoreLessContent,
} from './styled';
import { primaryDate, isValidDate } from 'helpers/date';
import { RecipientStatus, isRestrictedStatus } from 'store/recipients/types';
import { RestrictedCandidateBanner } from './RestrictedCandidate/RestrictedCandidateBanner';
import { ContributionBanner } from './ContributionBanner';
import { useAppSelector } from 'helpers/hooks';
import { getContributionSummariesByRecipientId } from 'selectors/contributionSummary';
import { trackEvent } from 'helpers/mixpanelEvents';
import type { CardVariant } from './types';

type CandidateCheck = (status?: string) => boolean;

interface VariantConfig {
  isActiveCandidate: boolean;
  showCompetitivePrimaryBanner: boolean;
  showRestrictedBanner: boolean;
  hideImpactScoreFor: CandidateCheck[];
}

const isCompetitivePrimary: CandidateCheck = status =>
  status === RecipientStatus.COMPETITIVE_PRIMARY;

const VARIANT_CONFIG: Record<CardVariant, VariantConfig> = {
  search: {
    isActiveCandidate: true,
    showCompetitivePrimaryBanner: true,
    showRestrictedBanner: true,
    hideImpactScoreFor: [isRestrictedStatus, isCompetitivePrimary],
  },
  page: {
    isActiveCandidate: false,
    showCompetitivePrimaryBanner: false,
    showRestrictedBanner: false,
    hideImpactScoreFor: [],
  },
};

interface Props {
  index: number;
  item: CandidateType;
  variant?: CardVariant;
  selectItem?: (id: number, check: boolean) => void;
  impactScoreVisibility?: boolean;
  showMoreVisibility?: boolean;
  primaryColor?: string;
  secondaryColor?: string;
  disabled?: boolean;
}

export const CandidateCard: FC<Props> = ({
  index,
  item,
  variant = 'page',
  selectItem,
  impactScoreVisibility,
  showMoreVisibility,
  primaryColor,
  secondaryColor,
  disabled = false,
}) => {
  const config = VARIANT_CONFIG[variant];
  const { isActiveCandidate, showCompetitivePrimaryBanner, showRestrictedBanner } = config;
  const [detailVisible, setDetailVisible] = useState(false);
  const { urlPartner, urlClientId } = getUrlParams();
  const [checkValue, setCheckValue] = useState(item.isSelected);
  const contributionSummary = useAppSelector(getContributionSummariesByRecipientId)[
    item.recipient_id
  ];
  const hasGiving = Boolean(contributionSummary && contributionSummary.cycle_total > 0);
  const isCandidateRestricted = isRestrictedStatus(item.recipient_status);
  const isCandidateCompetitivePrimary = isCompetitivePrimary(item.recipient_status);
  const shouldHideImpactScore = config.hideImpactScoreFor.some(check =>
    check(item.recipient_status)
  );
  const restrictedStatus =
    showRestrictedBanner && isRestrictedStatus(item.recipient_status)
      ? item.recipient_status
      : undefined;

  const handleCheck = () => {
    if (isCandidateRestricted || disabled) return;
    setCheckValue(!checkValue);
    if (selectItem) {
      selectItem(index, !checkValue);
    }
  };

  useEffect(() => {
    setCheckValue(item.isSelected);
  }, [item]);

  useEffect(() => {
    if (hasGiving) {
      trackEvent('Recipient Contribution Summary Viewed', {
        recipient_id: item.recipient_id,
      });
    }
  }, [item.recipient_id, hasGiving]);

  const showHideDetail = () => {
    setDetailVisible(prevState => !prevState);
  };

  return (
    <Container>
      <CardContent
        $justifyContent="space-between"
        $alignItems="center"
        $isSelected={isActiveCandidate || checkValue}
        $detailVisible={detailVisible}
      >
        <CandidateGrid>
          <GridCheckbox
            as="button"
            type="button"
            $pressable
            onClick={handleCheck}
            disabled={isCandidateRestricted || disabled}
            aria-label={
              isCandidateRestricted
                ? `${item.recipient_name} is not eligible to receive donations`
                : `Add ${item.recipient_name}`
            }
            title={
              isCandidateRestricted
                ? 'This candidate is not eligible to receive donations'
                : undefined
            }
          >
            {isActiveCandidate ? (
              <PlusContainer $color={primaryColor}>
                <Icon name={IconNames.Plus} size={14} color={secondaryColor} />
              </PlusContainer>
            ) : (
              <CheckBox value={checkValue} onChecked={noop} />
            )}
          </GridCheckbox>
          <CandidatePhoto $pressable={!disabled} onClick={handleCheck}>
            <Spacing $isVertical $size={8} />
            <StyledImage
              alt={`Recipient ${item.recipient_name}`}
              src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/recipients/rec_${
                item.misc_ids?.photo_id
              }.jpg`}
              fallbackImgType="candidate_square"
            />
          </CandidatePhoto>
          <Content $pressable={!disabled} onClick={handleCheck}>
            <Body>{item.recipient_name}</Body>
            {item.race?.election.display_info && (
              <Subtitle>{item.race?.election.display_info}</Subtitle>
            )}
            {item.race?.election.state && <Subtitle>{item.race?.election.state}</Subtitle>}
            {item.misc_ids?.org_summary && <Subtitle>{item.misc_ids?.org_summary}</Subtitle>}
            {item.misc_ids?.org_tag && <Subtitle>{item.misc_ids?.org_tag}</Subtitle>}
          </Content>
          {detailVisible && item.race && (
            <Details
              competitiveness={
                item.race?.election.ratings.length > 0 && item.race?.election.ratings[0]
                  ? item.race?.election.ratings[0]
                  : undefined
              }
              electionGoal={item.race?.election.election_goal}
              financialsUpdatedAt={item.race?.financials_updated_at}
              fundraisingTarget={item.race?.election.projected_cost}
              impactScore={shouldHideImpactScore ? undefined : item.impact_score}
              raised={item.race?.amount_raised}
              primaryElectionDate={
                isValidDate(item.race?.election.primary_election_date)
                  ? primaryDate(item.race?.election.primary_election_date)
                  : undefined
              }
              isCompetitivePrimary={showCompetitivePrimaryBanner && isCandidateCompetitivePrimary}
              restrictedStatus={restrictedStatus}
              hideImpactScore={shouldHideImpactScore}
              variant={variant}
            />
          )}
          <ShowMoreLessContent className="candidate-show-more-slot" onClick={showHideDetail}>
            {!shouldHideImpactScore &&
              ((impactScoreVisibility && item.impact_score) ||
                (!urlPartner && !urlClientId && item.impact_score)) && (
                <>
                  <Body $color={theme.colors.oathBlue}>
                    {parseFloat(item.impact_score).toFixed(1)}
                  </Body>
                  <Spacing $size={16} />
                </>
              )}
            {(showMoreVisibility || (!urlPartner && !urlClientId && item.impact_score)) && (
              <ShowMoreLessRow
                $pressable
                $alignItems="center"
                $detailVisible={!detailVisible}
                $justifyContent="flex-end"
              >
                <UtilityText>{!detailVisible ? 'Show More' : 'Show Less'}</UtilityText>
                <Spacing $isVertical $size={4} />
                <Icon
                  name={!detailVisible ? IconNames.PlusCircle : IconNames.MinusCircle}
                  size={14}
                  color={theme.colors.gray}
                />
              </ShowMoreLessRow>
            )}
          </ShowMoreLessContent>
        </CandidateGrid>
      </CardContent>
      {(showMoreVisibility || (!urlPartner && !urlClientId && item.impact_score)) && (
        <ShowMoreLessRow
          $pressable
          $alignItems="center"
          onClick={showHideDetail}
          $detailVisible={detailVisible}
          $detailOpen={true}
        >
          <UtilityText>{!detailVisible ? 'Show More' : 'Show Less'}</UtilityText>
          <Spacing $isVertical $size={4} />
          <Icon
            name={!detailVisible ? IconNames.PlusCircle : IconNames.MinusCircle}
            size={14}
            color={theme.colors.gray}
          />
        </ShowMoreLessRow>
      )}
      {hasGiving && (
        <ContributionBanner amountDollars={contributionSummary.cycle_total} variant="bar" />
      )}
      {showCompetitivePrimaryBanner && isCandidateCompetitivePrimary && (
        <CompetitivePrimaryBanner />
      )}
      {restrictedStatus && <RestrictedCandidateBanner recipientStatus={restrictedStatus} />}
    </Container>
  );
};
